import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Instagram as InstagramIcon, ArrowRight, Play } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Instagram strip. Supports two modes:
 *   - "graph": cached posts from the Instagram Graph API (tiles render here)
 *   - "snapwidget": a sandboxed iframe from snapwidget.com that auto-refreshes itself
 *
 * Auto-hides entirely when there's nothing to show.
 */
const InstagramStrip = ({ count = 6 }) => {
  const [data, setData] = useState({ mode: 'graph', items: [], iframe_src: null, enabled: false });
  const [loaded, setLoaded] = useState(false);
  const profileUrl = useSiteContent().instagram_url;

  useEffect(() => {
    axios
      .get(`${API}/api/instagram/feed?count=${count}`)
      .then((res) => setData(res.data || {}))
      .catch(() => setData({ enabled: false }))
      .finally(() => setLoaded(true));
  }, [count]);

  if (!loaded) return null;

  const isSnap = data.mode === 'snapwidget';
  const items = data.items || [];

  // Nothing to show in either mode
  if (!isSnap && items.length === 0) return null;
  if (isSnap && !data.iframe_src) return null;

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-white" data-testid="instagram-section">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 sm:mb-10">
          <div>
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-church-green tracking-wider uppercase mb-1">
              <InstagramIcon className="w-4 h-4" /> Instagram
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
              Follow along on Instagram
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-xl">
              See the latest from our church family.
            </p>
          </div>
          {profileUrl && <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-church-green hover:underline whitespace-nowrap"
            data-testid="instagram-view-all"
          >
            View profile <ArrowRight className="w-4 h-4" />
          </a>}
        </div>

        {isSnap ? (
          <div className="max-w-5xl mx-auto" data-testid="instagram-snapwidget">
            <iframe
              src={data.iframe_src}
              title="Instagram feed"
              className="w-full"
              style={{ border: 'none', overflow: 'hidden', minHeight: 420 }}
              scrolling="no"
              allowtransparency="true"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {items.map((item) => (
              <a
                key={item.ig_media_id}
                href={item.permalink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100 border border-slate-200 hover:border-church-green transition"
                data-testid="instagram-tile"
                title={(item.caption || '').slice(0, 80)}
              >
                {item.thumbnail_url && (
                  <img
                    src={item.thumbnail_url}
                    alt={(item.caption || '').slice(0, 100) || 'Life Church of New Jersey Instagram post'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                )}
                {item.is_video && (
                  <div className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1">
                    <Play className="w-3 h-3" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-2">
                  <p className="text-white text-xs line-clamp-2">{item.caption}</p>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InstagramStrip;
